import React, { useEffect } from "react";
import { View, StyleSheet, Alert } from "react-native";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { RichEditor, RichToolbar, actions } from "react-native-pell-rich-editor";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { useGetCourseByIdQuery, useEditCourseMutation } from "../../../graphql/generated/graphql";

const courseCatalogInfoSchema = z.object({
  price: z.string().optional(),
  description: z.string().min(1, "Description is required"),
  objectives: z.string().min(1, "Objectives are required"),
  targetAudience: z.string().optional(),
});

type CourseCatalogInfoFormData = z.infer<typeof courseCatalogInfoSchema>;

const CourseCatalogInfoContainer = ({ courseId }: { courseId: string }) => {
  const {
    data: courseData,
    loading,
    error,
    refetch,
  } = useGetCourseByIdQuery({
    variables: {
      id: courseId,
    },
  });

  const course = courseData?.getCourseById?.data?.[0];

  const [editCourse] = useEditCourseMutation();

  const editorRef = React.useRef<RichEditor>(null);

  useEffect(() => {
    if (course?.objectives && course?.objectives?.length > 0 && editorRef.current) {
      editorRef.current.setContentHTML(course?.objectives?.join("\n"));
    }
  }, [course?.objectives]);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
  } = useForm<CourseCatalogInfoFormData>({
    resolver: zodResolver(courseCatalogInfoSchema),
    defaultValues: {
      price: "0",
      description: "",
      objectives: "",
      targetAudience: "",
    },
  });

  useEffect(() => {
    if (course) {
      reset({
        price: course.cost?.toString() || "0",
        description: course.description || "",
        objectives: course.objectives?.join("\n") || "",
        targetAudience: course.targetAudience || "",
      });
    }
  }, [course, reset]);

  const handleSave = handleSubmit(async (formData) => {
    try {
      const response = await editCourse({
        variables: {
          id: course.id,
          course: {
            cost: formData.price ? Number(formData.price) : null,
            description: formData.description,
            objectives: [formData.objectives],
            targetAudience: formData.targetAudience,
          },
        },
      });

      refetch();
      Alert.alert("Success", "Course updated successfully");
      reset(formData);
    } catch (error) {
      console.error("Error saving course:", error);
      Alert.alert("Error", "Failed to save course changes. Please try again.");
    }
  });

  if (loading || !course) {
    return <CustomText text="Loading..." size="l" weight="ultraBold" style={styles.title} />;
  }

  if (error) {
    return <CustomText text="Error loading course" size="l" weight="ultraBold" style={styles.title} />;
  }

  return (
    <View style={styles.container}>
      <View
        style={{
          flexDirection: "column",
          justifyContent: "flex-start",
          maxWidth: 600,
          gap: 40,
          padding: 40,
          borderWidth: 1,
          borderColor: "#E5E7EB",
          borderRadius: 12,
        }}>
        <View style={styles.form}>
          <View style={styles.formGroup}>
            <CustomText text="Course Price" size="s" weight="ultraBold" style={{}} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              disabled={!course.stripeProductId}
              name="price"
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  editable={!!course.stripeProductId}
                  onChangeText={(text) => {
                    const newPrice = Number(text);

                    if (Number.isNaN(newPrice)) {
                      Alert.alert("Invalid price", "Please enter a valid number");
                      return;
                    }

                    if (newPrice < 0) {
                      Alert.alert("Invalid price", "Please enter a price greater than 0");
                      return;
                    }

                    onChange(newPrice.toString());
                  }}
                  placeholder="Course Price"
                  style={{}}
                  error={errors.price?.message}
                />
              )}
            />
            {errors.price && <CustomText text={errors.price.message} size="xs" weight="ultraBold" style={{}} />}
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Course Description" size="s" weight="ultraBold" style={{}} />
            <CustomText
              text="This information appears on the course details page in the course catalog."
              size="xs"
              style={styles.helperText}
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="description"
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  multiline={true}
                  onChangeText={onChange}
                  placeholder="Course Description"
                  style={{}}
                  error={errors.description?.message}
                />
              )}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="What will the student learn in this course?" size="s" weight="ultraBold" style={{}} />
            <CustomText
              text="This information appears on the course details page in the course catalog and is best formatted by using bullets."
              size="xs"
              style={styles.helperText}
            />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="objectives"
              render={({ field: { onChange, value } }) => (
                <View style={styles.richEditorContainer}>
                  <RichToolbar
                    editor={editorRef}
                    actions={[
                      actions.setBold,
                      actions.setItalic,
                      actions.setUnderline,
                      actions.heading1,
                      actions.heading2,
                      actions.insertBulletsList,
                      actions.insertOrderedList,
                      actions.insertLink,
                      actions.keyboard,
                      actions.setStrikethrough,
                      actions.alignLeft,
                      actions.alignCenter,
                      actions.alignRight,
                    ]}
                    style={styles.richToolbar}
                  />
                  <RichEditor
                    ref={editorRef}
                    style={styles.richEditor}
                    initialContentHTML={value}
                    onChange={(content) => setValue("objectives", content, { shouldDirty: true })}
                    placeholder="Add a description"
                  />
                </View>
              )}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Who is this course for?" size="s" weight="ultraBold" style={{}} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="targetAudience"
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  multiline={true}
                  onChangeText={onChange}
                  placeholder="Who is this course for?"
                  style={{}}
                  error={errors.targetAudience?.message}
                />
              )}
            />
          </View>
        </View>
        <View style={styles.footer}>
          <CustomButton
            text="Save"
            styleType="primary"
            size="m"
            onPress={() => handleSave()}
            leftIcon={null}
            rightIcon={null}
            style={{
              backgroundColor: !isDirty ? "#E5E7EB" : "#4F46E5",
              opacity: !isDirty ? 0.5 : 1,
            }}
            textStyle={{
              color: !isDirty ? "#6B7280" : "#FFFFFF",
            }}
            disabled={false}
            textProps={{}}
            useTranslationText={true}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    backgroundColor: "#fff",
    width: 600,
    marginHorizontal: "auto",
    flexDirection: "column",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  title: {
    fontSize: 24,
    color: "#111827",
  },
  form: {
    flex: 1,
    maxWidth: 600,
  },
  formGroup: {
    width: "auto",
  },
  helperText: {
    color: "#6B7280",
    marginTop: 4,
  },
  richEditorContainer: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    overflow: "hidden",
  },
  richEditor: {
    minHeight: 200,
    maxHeight: 400,
  },
  richToolbar: {
    borderTopWidth: 1,
    borderTopColor: "#D1D5DB",
    backgroundColor: "#F9FAFB",
  },
});

export default CourseCatalogInfoContainer;
